import React from 'react';
import { motion } from 'framer-motion';
import Footer from '../../home/components/Footer';
import Icon from '../../../components/AppIcon';

const primaryColorClass = 'text-[#1e40af]';
const primaryBgClass = 'bg-[#1e40af]';
const primaryHoverBgClass = 'hover:bg-[#153488]';
const primaryBorderClass = 'border-[#1e40af]';
const primaryBorderHalfClass = 'border-[#1e40af]/50';
const primaryBorderThirdClass = 'border-[#1e40af]/30';

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2,
        },
    },
};

const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: 'easeOut',
        },
    },
};

const iconBounce = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            type: 'spring',
            stiffness: 100,
            damping: 10,
        },
    },
};

const pageContent = {
    hero: {
        title: 'Shape the Future With Us',
        subtitle: "We're looking for passionate individuals who share our vision of innovation and excellence. Join us to build innovative solutions for clients and make a meaningful impact in the world of technology.",
    },
    whyChoose: {
        title: 'Why Choose Innovate Inc.',
        subtitle: 'We believe in creating an environment where talent thrives and innovation flourishes.',
        cards: [
            {
                icon: 'Rocket',
                title: 'Innovation First',
                description: 'We encourage bold ideas and provide the resources necessary to bring truly groundbreaking concepts to success.',
            },
            {
                icon: 'ChartBar',
                title: 'Growth Opportunities',
                description: 'Continuous learning and career development are at the heart of our employee experience.',
            },
            {
                icon: 'Scale',
                title: 'Work-Life Balance',
                description: 'We value your well-being and offer flexible arrangements to support your personal life and productivity.',
            },
        ],
    },
    openings: {
        title: 'Join Our Growing Team',
        subtitle: 'Explore our latest opportunities across various departments.',
        jobs: [
            {
                title: 'Senior Frontend Developer',
                type: 'Full-time • Remote',
                description: 'Empower our flagship product and content developer to join our engineering team and help build next-generation web applications.',
                tags: ['React', 'Next.js', 'Typescript'],
                new: true,
            },
            {
                title: 'UX/UI Designer',
                type: 'Full-time • On-site',
                description: 'Join our design team to create intuitive and beautiful user experiences that delight our customers.',
                tags: ['Figma', 'Adobe XD', 'Prototyping'],
                new: false,
            },
            {
                title: 'Product Manager',
                type: 'Full-time • On-site',
                description: 'Lead the strategy and feature development for our flagship platform, working closely with engineering and design teams.',
                tags: ['Roadmap', 'Agile', 'Analytics'],
                new: false,
            },
        ],
    },
    benefits: {
        title: 'We Take Care of Our Team',
        subtitle: 'Comprehensive benefits package designed to support your health, growth, and happiness.',
        cards: [
            {
                icon: 'Heart',
                title: 'Health & Wellness',
                description: 'Comprehensive medical, dental, and vision, plus wellness programs.',
            },
            {
                icon: 'GraduationCap',
                title: 'Learning Budget',
                description: 'Access to funds for additional, career-aligned professional development.',
            },
            {
                icon: 'Clock',
                title: 'Flexible Hours',
                description: 'Work when you are most productive with our flexible scheduling options.',
            },
            {
                icon: 'Sun',
                title: 'Unlimited PTO',
                description: 'Take the time you need to recharge and maintain work-life balance.',
            },
        ],
    },
    howToApply: {
        title: 'Your Journey to Join Us',
        subtitle: 'Our streamlined application process is designed to welcome you better.',
        steps: [
            { icon: 'Router', title: 'Apply Online', description: 'Submit your application through our careers portal with your resume and Portfolio.' },
            { icon: 'Phone', title: 'Initial Review', description: 'Our team reviews your application and reaches out within 48 hours.' },
            { icon: 'Users', title: 'Interview Process', description: 'Meet the team through virtual or in-person interviews.' },
            { icon: 'Sparkles', title: 'Welcome Aboard', description: 'Receive your offer and start making an impact on day one.' },
        ],
    },
};

const JobOpening = ({ job }) => (
    <motion.div
        variants={fadeIn}
        className={`bg-white p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-2 border-blue-500 relative rounded-md`}
    >
        {job.new && (
            <span className="absolute top-5 right-0 -mt-3 mr-4 px-3 py-1 bg-green-200 text-green-600 rounded-md text-xs font-bold uppercase tracking-wider">
                New
            </span>
        )}
        <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
        <p className={`text-sm font-medium ${primaryColorClass} mb-3`}>{job.type}</p>
        <p className="text-gray-600 text-sm mb-4">{job.description}</p>
        <div className="flex flex-wrap gap-2 mb-4">
            {job.tags.map((tag, index) => (
                <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                    {tag}
                </span>
            ))}
        </div>
        {/* <button className={`${primaryColorClass} font-semibold text-sm hover:text-[#153488] transition duration-300 flex items-center`}>
            View Details
        </button> */}
    </motion.div>
);

const BenefitCard = ({ card }) => (
    <motion.div
        variants={iconBounce}
        className="text-center p-6"
    >
        <div className="w-10 h-10 mx-auto mb-4 flex items-center justify-center">
            <Icon name={card.icon} className={`mx-auto ${primaryColorClass} w-10 h-10`} />
        </div>
        <h3 className="text-lg font-bold mb-2 text-gray-900">{card.title}</h3>
        <p className="text-sm text-gray-600">{card.description}</p>
    </motion.div>
);

const ProcessStep = ({ step, index }) => (
    <motion.div
        variants={iconBounce}
        className="text-center p-4"
    >
        <div className="relative inline-block mb-4">
            <div className={`w-16 h-16 flex items-center justify-center mx-auto rounded-full border-2 ${primaryBorderThirdClass} bg-blue-50`}>
                <Icon name={step.icon} className={`${primaryColorClass} w-10 h-10`} />
            </div>
        </div>
        <h3 className="font-bold text-lg mb-2 text-gray-900">{step.title}</h3>
        <p className="text-sm text-gray-600">{step.description}</p>
    </motion.div>
);

const WhyChooseCard = ({ card }) => (
    <motion.div variants={fadeIn} className={`bg-white p-8 rounded-md `}>
        <div className="w-10 h-10 mb-4 flex items-center justify-center">
            <Icon name={card.icon} className={`${primaryColorClass} w-10 h-10`} />
        </div>
        <h3 className="text-xl font-bold mb-2 text-gray-900">{card.title}</h3>
        <p className="text-gray-600 text-base">{card.description}</p>
    </motion.div>
);


const CareersPage = () => {
    const Nav = () => (
        <header className="fixed top-0 left-0 right-0 bg-white  z-10">
            <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
                <div className={`text-2xl font-bold ${primaryColorClass}`}>Innovate Inc.</div>
                <nav className="hidden md:flex space-x-6 text-gray-600">
                    {['Home', 'About Us', 'Services', 'Careers', 'Contact'].map((item) => (
                        <a key={item} href="#" className={`hover:${primaryColorClass} ${item === 'Careers' ? 'font-bold text-gray-900' : ''}`}>
                            {item}
                        </a>
                    ))}
                </nav>
                <button className={`${primaryBgClass} text-white px-4 py-2 font-semibold ${primaryHoverBgClass} transition duration-300`}>
                    Get Started
                </button>
            </div>
        </header>
    );

    return (
        <div className="w-full bg-white font-sans text-gray-900 antialiased overflow-x-hidden pt-20">

            <Nav />

            <section className="text-center max-w-4xl mx-auto px-6 pt-10 pb-8">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <p className="uppercase text-sm font-semibold text-gray-500 tracking-widest mb-3">JOIN OUR TEAM</p>
                    <h1 className="text-5xl md:text-6xl font-extrabold leading-tight tracking-tighter mb-6 text-gray-900">
                        {pageContent.hero.title}
                    </h1>
                    <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
                        {pageContent.hero.subtitle}
                    </p>
                    <div className="flex justify-center space-x-4">
                        <button className={`${primaryBgClass} text-white px-8 py-3 font-semibold ${primaryHoverBgClass} transition duration-300`}>
                            View Open Positions
                        </button>
                    </div>
                </motion.div>
            </section>

            <section className="bg-gray-50 py-16 md:py-6">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-12"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeIn}
                    >
                        <p className="uppercase text-sm font-semibold text-gray-500 tracking-widest mb-2">OUR CULTURE</p>
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
                            {pageContent.whyChoose.title}
                        </h2>
                        <p className="text-base text-gray-600 max-w-xl mx-auto">
                            {pageContent.whyChoose.subtitle}
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid md:grid-cols-3 gap-8"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        {pageContent.whyChoose.cards.map((card, index) => (
                            <WhyChooseCard key={index} card={card} />
                        ))}
                    </motion.div>
                </div>
            </section>

            <section className="py-16 md:py-8">
                <div className="max-w-4xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-12"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeIn}
                    >
                        <p className="uppercase text-sm font-semibold text-gray-500 tracking-widest mb-2">CURRENT OPENINGS</p>
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
                            {pageContent.openings.title}
                        </h2>
                        <p className="text-base text-gray-600 max-w-xl mx-auto">
                            {pageContent.openings.subtitle}
                        </p>
                    </motion.div>

                    <motion.div
                        className="space-y-6"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                    >
                        {pageContent.openings.jobs.map((job, index) => (
                            <JobOpening key={index} job={job} />
                        ))}
                    </motion.div>

                    <motion.div
                        className="text-center mt-8"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeIn}
                    >
                        {/* <button className="bg-gray-900 text-white px-10 py-3 font-semibold hover:bg-gray-700 transition duration-300 tracking-wider">
                            View All Positions
                        </button> */}
                    </motion.div>
                </div>
            </section>

            <section className="bg-gray-50 py-16 md:py-8    ">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-8"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeIn}
                    >
                        <p className="uppercase text-sm font-semibold text-gray-500 tracking-widest mb-2">EMPLOYEE BENEFITS</p>
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
                            {pageContent.benefits.title}
                        </h2>
                        <p className="text-base text-gray-600 max-w-xl mx-auto">
                            {pageContent.benefits.subtitle}
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                    >
                        {pageContent.benefits.cards.map((card, index) => (
                            <BenefitCard key={index} card={card} />
                        ))}
                    </motion.div>
                </div>
            </section>

            <section className="py-16 md:py-8">
                <div className="max-w-5xl mx-auto px-6">
                    <motion.div
                        className="text-center mb-6"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.5 }}
                        variants={fadeIn}
                    >
                        <p className="uppercase text-sm font-semibold text-gray-500 tracking-widest mb-2">HOW TO APPLY</p>
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
                            {pageContent.howToApply.title}
                        </h2>
                        <p className="text-base text-gray-600 max-w-xl mx-auto">
                            {pageContent.howToApply.subtitle}
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12 relative"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                    >
                        <div className="hidden lg:block absolute"></div>
                        {pageContent.howToApply.steps.map((step, index) => (
                            <ProcessStep key={index} step={step} index={index} />
                        ))}
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default CareersPage;